import React from 'react';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  GraphDevice,
  STACKLIGHT_CHANNEL_COUNT,
  actuatorChannelOptions,
  stacklightChannelRefs,
} from '../flow/graph';
import { useI18n } from '../i18n';

type ChannelRefsFieldProps = {
  device: GraphDevice;
  existing: GraphDevice[];
  disabled?: boolean;
  onChange: (channels: string[]) => void;
};

const ChannelRefsField: React.FC<ChannelRefsFieldProps> = ({
  device,
  existing,
  disabled,
  onChange,
}) => {
  const { t } = useI18n();
  const options = actuatorChannelOptions(existing.filter((item) => item.id !== device.id));
  const rows = stacklightChannelRefs(device);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {options.length < STACKLIGHT_CHANNEL_COUNT ? (
        <Alert severity="warning">
          {t('deviceConfig.needChannels')}
          {' '}
          {t('deviceConfig.needChannelsCount', { count: options.length })}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">{t('deviceConfig.needChannels')}</Typography>
      )}
      {rows.map((row, index) => {
        const taken = new Set(rows.filter((_, other) => other !== index && rows[other]));
        return (
          <TextField
            key={index}
            select
            size="small"
            fullWidth
            label={t('deviceConfig.channelRef', { index: index + 1 })}
            value={row}
            disabled={disabled || options.length === 0}
            onChange={(e) => {
              const next = [...rows];
              next[index] = e.target.value;
              onChange(next);
            }}
          >
            <MenuItem value="">{t('deviceConfig.channelPick')}</MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.id} value={opt.id} disabled={taken.has(opt.id)}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        );
      })}
    </Box>
  );
};

export default ChannelRefsField;
